import { Eye } from 'lucide-react-native';
import { memo, type ReactNode } from 'react';
import type { ImageSourcePropType } from 'react-native';
import { twJoin } from 'tailwind-merge';

import { Avatar, AvatarImage } from '@/components/ui/avatar';
import { Box } from '@/components/ui/box';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Pressable } from '@/components/ui/pressable';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { PRIMUM_SUPPORT_USER } from '@/constants/users';
import { getAccessibilityProps } from '@/util/accessibilityProps';

type ActionBubbleProps = {
  accessibilityLabel: string;
  testID?: string;
  children: ReactNode;
  avatarSource?: ImageSourcePropType;
  authorName?: string;
  /** Called when the user taps the dismiss button. Omit to hide the button. */
  onDismiss?: () => void;
  /** Optional content rendered left-aligned in the footer row alongside the dismiss button. */
  footer?: ReactNode;
  /** When true, renders a "Beta" badge next to the author name. */
  showBetaBadge?: boolean;
  /** When true, renders an "Only you" visibility badge in the header row. */
  showVisibilityBadge?: boolean;
  /** When true, renders a "Primum" badge in the header row for Primum-only messages. */
  showPrimumBadge?: boolean;
  /** When true, bubble chrome shrink-wraps to its content instead of filling the row. */
  shrinkToContent?: boolean;
  /** When true, hides the avatar and aligns the bubble to the end (right). */
  alignEnd?: boolean;
};

/**
 * Generic outer shell for conversation action bubbles.
 *
 * Renders an avatar, author name, and a rounded container box.
 * Defaults to Primum Support branding; callers can override.
 * Inner content is fully controlled by the caller via children.
 *
 * This is the one place Primum Support's look is defined, so every support
 * message reads as the same thing wherever it is composed. Change it here
 * rather than restyling a caller.
 *
 * The border is the brand `primary-500` rather than a neutral: it is what
 * distinguishes support copy from a participant's own bubble, and it is held to
 * the 3:1 non-text boundary in both themes, which the neutral it replaced did
 * not clear in light. The background is stated explicitly so the card is white
 * on its own terms instead of borrowing whatever it happens to sit on.
 */
export const ActionBubble = memo(function ActionBubble({
  avatarSource = { uri: PRIMUM_SUPPORT_USER.avatarSrc },
  authorName = PRIMUM_SUPPORT_USER.name,
  accessibilityLabel,
  testID,
  children,
  onDismiss,
  footer,
  showBetaBadge,
  showVisibilityBadge,
  showPrimumBadge,
  shrinkToContent = false,
  alignEnd = false,
}: ActionBubbleProps) {
  return (
    <HStack
      space="sm"
      className={twJoin(
        'mb-3 min-w-0 max-w-full items-end',
        alignEnd ? 'self-end' : 'self-start',
        !shrinkToContent && 'w-full'
      )}
      testID={testID}
      {...getAccessibilityProps({ label: accessibilityLabel, accessible: true })}>
      {!alignEnd && (
        <Avatar size="sm" className="mr-1">
          <AvatarImage source={avatarSource} />
        </Avatar>
      )}

      <VStack
        className={twJoin('min-w-0', shrinkToContent ? 'max-w-full shrink' : 'max-w-full flex-1')}>
        <HStack className="mb-1 max-w-full flex-row flex-wrap items-center gap-1.5">
          <Text size="xs" className="min-w-0 shrink text-typography-700">
            {authorName}
          </Text>
          {showBetaBadge && (
            <Box className="rounded-full bg-background-info px-2 py-0.5">
              <Text size="2xs" className="font-medium uppercase text-info-600">
                Beta
              </Text>
            </Box>
          )}
          {showVisibilityBadge && (
            <HStack
              space="xs"
              className="items-center rounded-full bg-highlight-50 px-2 py-0.5"
              {...getAccessibilityProps({ label: 'Only visible to you', accessible: true })}>
              <Icon as={Eye} size="2xs" className="text-highlight-800" />
              <Text size="2xs" className="font-medium text-highlight-800">
                Only you
              </Text>
            </HStack>
          )}
          {showPrimumBadge && (
            <HStack
              space="xs"
              className="items-center rounded-full bg-primary-50 px-2 py-0.5"
              {...getAccessibilityProps({ label: 'Visible to Primum only', accessible: true })}>
              <Icon as={Eye} size="2xs" className="text-primary-700" />
              <Text size="2xs" className="font-medium text-primary-700">
                Primum
              </Text>
            </HStack>
          )}
        </HStack>

        <Box
          className={twJoin(
            'min-w-0 max-w-full rounded-2xl border border-primary-500 bg-background-0 p-3',
            !shrinkToContent && 'w-full'
          )}>
          {children}
          {(footer || onDismiss) && (
            <HStack className="mt-2 items-center">
              {footer && <Box className="flex-1 items-start">{footer}</Box>}
              {onDismiss && (
                <Pressable
                  onPress={onDismiss}
                  className="ml-auto rounded px-2 py-1 active:bg-background-200"
                  {...getAccessibilityProps({ label: 'Dismiss', role: 'button' })}>
                  <Text size="sm" className="text-primary-600 underline">
                    Dismiss
                  </Text>
                </Pressable>
              )}
            </HStack>
          )}
        </Box>
      </VStack>
    </HStack>
  );
});
